import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { getCategoryFromTranslatedSlug } from '../../constants/categorySlug.constants';
import { getBrandFromSlug, getBrandLabelFromSlug } from '../../util/brandSlug';
import { normalizeApiBrandSlug } from '../../util/seoSlug';
import { getTypeDisplayName, getTypePageFromSlug } from '../../util/typeSlug';
import { useIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { useConfiguration } from '../../context/configurationContext';
import SearchPageWithGrid from '../SearchPage/SearchPageWithGrid';
import SearchPageWithMap from '../SearchPage/SearchPageWithMap';
import PopularBrandModelsRail from './PopularBrandModelsRail';

/**
 * Query params that may appear on a boat-type page without it counting as the
 * visitor having filtered. Anything else makes the URL a filtered variant of
 * the landing page, which must not be indexed alongside the clean one.
 */
const NON_FILTER_PARAMS = ['page'];

/**
 * SEOPage component - handles category, brand and boat-type pages
 * This page is SEO-friendly and uses Dutch category slugs (e.g., /zeilboten),
 * brand slugs (e.g., /linssen) or boat-type slugs (e.g., /motorjachten)
 *
 * All three share this single-segment route, so the slug itself says nothing
 * about which kind of page it is — it is resolved against three separate
 * dictionaries, in the order category -> brand -> type. See util/typeSlug.js
 * for why colliding slugs are excluded from the type set rather than left to
 * that ordering.
 *
 * The loadData function in SearchPage.duck.js reads the slug from route params
 * and applies the appropriate filters. The SearchPage.shared.js helpers also read
 * from route params to display filters correctly in the UI.
 * This keeps the URL clean like /Andaman without ?manufacturer=ANDAMAN
 */
const SEOPageComponent = props => {
  const { brandPage, pageStatus } = props;
  const { slug } = useParams();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const config = useConfiguration();
  const intl = useIntl();

  useEffect(() => {
    // Validate that the slug is a valid category, brand or boat type (case-insensitive)
    const normalizedSlug = slug?.toLowerCase();
    const category = getCategoryFromTranslatedSlug(normalizedSlug, 'nl-NL');
    const brandValue = getBrandFromSlug(normalizedSlug);
    const typePage = getTypePageFromSlug(normalizedSlug, 'nl-NL');

    // A brand the curated LISTING_BRANDS list does not know about still resolves
    // if the aggregation wrote a `brand` document for it — SEOPage.duck fetched
    // it. Only redirect once the API has definitively said 404; while the fetch
    // is in flight or if it failed with a 500, leave the URL alone rather than
    // bouncing a page that is probably valid.
    if (!category && !brandValue && !typePage && !brandPage && pageStatus === 404) {
      history.replace('/te-koop');
    }
    // If valid, don't redirect - let loadData and SearchPage helpers handle the filtering from route params
  }, [slug, history, brandPage, pageStatus]);

  // Use the same search page component based on config
  const SearchPage =
    config?.searchPage?.variantType === 'map' ? SearchPageWithMap : SearchPageWithGrid;

  // If the slug resolves to a brand (not a category), render the "Populaire
  // modellen" rail above the search results. Map variant doesn't accept the
  // extraResultsHeader prop yet — the rail is grid-only for now.
  const normalizedSlug = slug?.toLowerCase();
  const brandValueForRail = useMemo(() => getBrandFromSlug(normalizedSlug), [normalizedSlug]);
  const brandLabelForRail = useMemo(() => getBrandLabelFromSlug(normalizedSlug), [normalizedSlug]);

  // The rail queries the seo-pages API, which keys brands by its own slug
  // normalization — derive it from the canonical manufacturer value rather than
  // reusing the URL slug, which differs for brands containing punctuation or
  // diacritics (see util/seoSlug.js).
  const apiBrandSlug = useMemo(() => normalizeApiBrandSlug(brandValueForRail), [
    brandValueForRail,
  ]);

  // An API-resolved brand already carries the API's own slug and label, so it
  // needs no normalization round-trip.
  const railBrandSlug = brandPage?.brandSlug || apiBrandSlug;
  const railBrandLabel = brandPage?.brandLabel || brandLabelForRail;

  const extraResultsHeader =
    railBrandSlug && SearchPage === SearchPageWithGrid ? (
      <PopularBrandModelsRail brandSlug={railBrandSlug} brandLabel={railBrandLabel} />
    ) : null;

  // Boat-type pages are the one variant here that opts into being indexed, so
  // they get real metadata. Category and brand pages keep whatever robots
  // behaviour they have today (noIndex left unset -> Page's default of true).
  const typePage = useMemo(() => getTypePageFromSlug(normalizedSlug, 'nl-NL'), [normalizedSlug]);

  const typeSeo = useMemo(() => {
    if (!typePage) return null;

    // Filters layer onto this URL as query params rather than redirecting, so
    // the filtered variants have to be kept out of the index — otherwise
    // /motorjachten?type=... competes with /motorjachten for the same results.
    const searchParams = parse(location.search);
    const isFiltered = Object.keys(searchParams).some(
      key => !NON_FILTER_PARAMS.includes(key) && searchParams[key] !== ''
    );

    // Derived from the slug, not the `General.listing.type.*` label: those are
    // singular ("Motorjacht") while the slugs were written as the plural nouns
    // a landing page wants ("motorjachten").
    const typeName = getTypeDisplayName(typePage.slug);

    return {
      title: intl.formatMessage({ id: 'SEOPage.boatType.title' }, { type: typeName }),
      description: intl.formatMessage(
        { id: 'SEOPage.boatType.description' },
        { type: typeName }
      ),
      // The page's h1. Kept free of the listing count, which changes between
      // crawls and would make the heading look volatile.
      heading: intl.formatMessage({ id: 'SEOPage.boatType.heading' }, { type: typeName }),
      noIndex: isFiltered,
    };
  }, [typePage, location.search, intl]);

  // An API-resolved brand page gets real metadata for the same reason boat-type
  // pages do: these URLs did not exist before, so there is no established robots
  // behaviour to preserve. Curated-brand and category pages are left untouched.
  const brandSeo = useMemo(() => {
    if (!brandPage) return null;

    const searchParams = parse(location.search);
    const isFiltered = Object.keys(searchParams).some(
      key => !NON_FILTER_PARAMS.includes(key) && searchParams[key] !== ''
    );

    const brand = brandPage.brandLabel;
    return {
      title: intl.formatMessage({ id: 'SEOPage.brand.title' }, { brand }),
      description: intl.formatMessage({ id: 'SEOPage.brand.description' }, { brand }),
      heading: intl.formatMessage({ id: 'SEOPage.brand.heading' }, { brand }),
      // A brand with a single listing resolves but is not worth indexing; the
      // API decides that via isPublished. A filtered variant is never indexed.
      noIndex: isFiltered || !brandPage.isPublished || !!brandPage.isStale,
    };
  }, [brandPage, location.search, intl]);

  // Pass match prop so SearchPage can read route params for filter display.
  // `seo` carries the pagination target for every variant: without it, page 2 of
  // /linssen links back to /te-koop and loses the brand.
  return (
    <SearchPage
      {...props}
      match={match}
      extraResultsHeader={extraResultsHeader}
      seo={{
        pageName: 'SEOPage',
        pagePathParams: { slug },
        ...(typeSeo || {}),
        ...(brandSeo || {}),
      }}
    />
  );
};

const mapStateToProps = state => ({
  brandPage: state.SEOPage?.brandPage,
  pageStatus: state.SEOPage?.pageStatus,
});

export default connect(mapStateToProps)(SEOPageComponent);
